import * as React from "react";

import { cn } from "@/lib/utils";

// Drop-in replacement for a single-line <input> that WRAPS overflowing text onto new lines
// and grows its own height to fit — a native <input> can only clip or scroll horizontally.
//
// Implemented with the "replicated content" grid trick: an invisible twin holding the same
// text (styled identically) sizes a single-cell grid to the wrapped height, and the textarea
// is stretched to fill that same cell. No refs, effects, or measurement — the height is
// always correct, even on first paint. On a slide the extra height is absorbed by the
// flex-1 content region below, so the fixed 1600x900 canvas stays constant (§1.4).
const AutoGrowTextarea = React.forwardRef<
  HTMLTextAreaElement,
  React.ComponentProps<"textarea"> & { containerClassName?: string }
>(({ className, containerClassName, style, value, ...props }, ref) => {
  // Everything that affects wrapped height must be identical on the twin and the textarea:
  // font (from inline `style`), padding/border (from `className`), line-height, wrap mode.
  const boxClass = cn(
    className,
    "col-start-1 row-start-1 m-0 whitespace-pre-wrap break-words leading-tight",
  );
  return (
    // grid-cols-1 (minmax(0,1fr)) makes the cell exactly the container width, so the twin
    // wraps where the textarea does. Callers pass flex/width sizing via containerClassName.
    <div className={cn("grid grid-cols-1", containerClassName ?? "w-full")}>
      <div className={cn(boxClass, "invisible")} style={style} aria-hidden>
        {/* Trailing space keeps a trailing newline (and the empty value) on its own row. */}
        {`${value ?? ""} `}
      </div>
      <textarea
        {...props}
        ref={ref}
        value={value}
        rows={1}
        style={style}
        className={cn(boxClass, "resize-none overflow-hidden")}
      />
    </div>
  );
});
AutoGrowTextarea.displayName = "AutoGrowTextarea";

export { AutoGrowTextarea };
